import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { ConfirmModal } from 'components/ConfirmModal';
import {
  formatResultCount,
  ManagementDateRangeFilter,
  ManagementFilterBar,
  ManagementPagination,
  ManagementSearchInput,
  ManagementSelectFilter,
} from 'components/ManagementFilters';
import { Snackbar } from 'components/Snackbar';
import { pageCardClasses } from 'components/AppLayout/styles';
import { routes } from 'constants/routes';
import { adCodeStrings, commonStrings } from 'constants/strings';
import { useAdCodeMedia, useAdCodeMutations, useAdCodes } from 'hooks';
import { useOrgPath } from 'hooks/useOrgPath';
import { toErrorMessage } from 'hooks/utils';
import type {
  TAdCodeListItem,
  TAdCodeMedium,
  TAdCodeStatus,
  TListAdCodesRequest,
} from 'types';
import {
  buildMediumOptions,
  formatCount,
  formatEstimated,
  formatRate,
  statusOptions,
} from './adCodeDisplay';
import { buildDefaultRange } from './defaultRange';
import { AdCodesCardList } from './AdCodesCardList';
import { AdCodesTable } from './AdCodesTable';

const PAGE_SIZE = 20;

type LocationState = { snackbarMessage?: string };

export default function AdCodes() {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const location = useLocation();

  const [snackbarMessage, setSnackbarMessage] = useState<string | undefined>();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<TAdCodeListItem | null>(
    null,
  );
  const [actionError, setActionError] = useState<string>();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<TAdCodeStatus | ''>('ACTIVE');
  const [medium, setMedium] = useState<TAdCodeMedium | ''>('');
  const mediaQuery = useAdCodeMedia();
  const mediumOptions = buildMediumOptions([medium, ...mediaQuery.media]);
  // 既定は直近30日。無期間だと全期間の帰属を集計することになり重い（設計5.2）。
  const [range, setRange] = useState(() => buildDefaultRange());
  const [excludeBlocked24h, setExcludeBlocked24h] = useState(false);

  const filterResetKey = JSON.stringify({
    search,
    status,
    medium,
    range,
    excludeBlocked24h,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    resetKey: filterResetKey,
  });
  // 絞り込みが変わった瞬間に render 中で 1 ページ目へ同期リセットし、旧ページの
  // まま重い集計クエリを一度無駄撃ちするのを防ぐ（友だち一覧と同じ手法）
  const page = pagination.resetKey === filterResetKey ? pagination.page : 1;

  const params = useMemo<TListAdCodesRequest>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      ...(search ? { q: search } : {}),
      ...(status ? { status } : {}),
      ...(medium ? { medium } : {}),
      // 日付だけを渡し、日本時間の日境界への変換はサーバー側に任せる
      ...(range.from ? { from: range.from } : {}),
      ...(range.to ? { to: range.to } : {}),
      ...(excludeBlocked24h ? { excludeBlocked24h: true } : {}),
    }),
    [page, search, status, medium, range, excludeBlocked24h],
  );

  const { data, loading, error } = useAdCodes(params);
  const { archive } = useAdCodeMutations();
  const archivingId = archive.isPending
    ? archive.variables?.adCodeId
    : undefined;
  // 既定の状態フィルタは「有効」なので、アーカイブ済みだけの組織や管理名の検索でも
  // 0件になる。「まだ1件も無い」と言い切れるのは totalAll を受け取れたときだけで、
  // 取得前・失敗時に言い切ると、実際には在るコードが無いように見える
  const hasNoAdCode = data?.totalAll === 0;
  const emptyMessage = hasNoAdCode
    ? adCodeStrings.empty.title
    : commonStrings.filteredEmpty;
  // 「有効」で絞って0件のとき、コードがアーカイブ済みへ移っただけなら件数で知らせる。
  // 状態が「すべて」「アーカイブ」で0件なら archivedTotal も必ず0になる
  const archivedHint =
    data && data.total === 0 && data.archivedTotal > 0
      ? adCodeStrings.empty.archivedHint.replace(
          '$count',
          String(data.archivedTotal),
        )
      : undefined;

  useEffect(() => {
    const message = (location.state as LocationState | null)?.snackbarMessage;
    if (!message) return;
    setSnackbarMessage(message);
    setIsSnackbarOpen(true);
    navigate(orgPathFor(routes.AD_CODES), { replace: true, state: null });
  }, [location.state, navigate, orgPathFor]);

  const items = data?.items ?? [];
  const summary = data?.summary;
  const totalPage = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 0;

  // 最終ページの最後のコードをアーカイブした場合は、存在するページへ戻す。
  useEffect(() => {
    if (!loading && data && page > totalPage) {
      setPagination({ page: totalPage, resetKey: filterResetKey });
    }
  }, [data, filterResetKey, loading, page, totalPage]);

  const handleOpen = (adCodeId: number) =>
    navigate(orgPathFor(routes.AD_CODES_DETAIL.replace(':adCodeId', String(adCodeId))));

  const handleEdit = (adCodeId: number) =>
    navigate(orgPathFor(routes.AD_CODES_EDIT.replace(':adCodeId', String(adCodeId))));

  const handleArchive = (adCodeId: number, archived: boolean) => {
    if (archive.isPending) return;
    setActionError(undefined);
    archive.mutate(
      { adCodeId, archived },
      {
        onSuccess: () => {
          setConfirmArchive(null);
          setSnackbarMessage(
            archived
              ? adCodeStrings.messages.archived
              : adCodeStrings.messages.unarchived,
          );
          setIsSnackbarOpen(true);
        },
        onError: (mutationError) => {
          setConfirmArchive(null);
          setActionError(
            toErrorMessage(mutationError, adCodeStrings.errors.archiveFailed),
          );
        },
      },
    );
  };

  const handleToggleArchive = (adCode: TAdCodeListItem) => {
    if (archive.isPending) return;
    setActionError(undefined);
    setIsSnackbarOpen(false);
    if (adCode.status === 'ACTIVE') {
      setConfirmArchive(adCode);
    } else {
      handleArchive(adCode.id, false);
    }
  };

  // コピー自体は AdCodeCopyUrlButton が行う。ここは完了の通知だけ。
  const handleCopyUrl = () => {
    setSnackbarMessage(adCodeStrings.messages.urlCopied);
    setIsSnackbarOpen(true);
  };

  const summaryTiles = summary
    ? [
        {
          label: adCodeStrings.metrics.adCodeCount,
          value: formatCount(data?.total ?? 0),
        },
        {
          label: adCodeStrings.metrics.click,
          value: formatCount(summary.clickCount),
        },
        {
          label: adCodeStrings.metrics.registration,
          value: formatCount(summary.confirmedCount),
          sub: formatEstimated(summary),
        },
        {
          label: adCodeStrings.metrics.registrationRate,
          value: formatRate(summary.registrationRate),
        },
        {
          label: adCodeStrings.metrics.blocked24h,
          value: formatCount(summary.blocked24hCount),
        },
        {
          label: adCodeStrings.metrics.blocked,
          value: formatCount(summary.blockedCount),
        },
      ]
    : [];

  return (
    <>
      <section className={`w-full ${pageCardClasses.regular}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl text-ink">
              {adCodeStrings.title}
            </h1>
            <p className="mt-1 text-sm text-muted">
              {adCodeStrings.description}
            </p>
          </div>
          <Button
            type="button"
            onClick={() => navigate(orgPathFor(routes.AD_CODES_CREATE))}
          >
            {adCodeStrings.create}
          </Button>
        </div>

        <div className="mt-6">
          <ManagementFilterBar>
            <ManagementDateRangeFilter
              label={adCodeStrings.filters.period}
              from={range.from}
              to={range.to}
              onChange={setRange}
              onClear={() => setRange(buildDefaultRange())}
            />
            <ManagementSelectFilter
              label={adCodeStrings.filters.status}
              value={status}
              options={statusOptions}
              onChange={(value) => setStatus(value as TAdCodeStatus | '')}
              onClear={() => setStatus('')}
            />
            <ManagementSelectFilter
              label={adCodeStrings.filters.medium}
              className="max-w-full"
              value={medium}
              options={mediumOptions}
              onChange={setMedium}
              onClear={() => setMedium('')}
            />
            <ManagementSearchInput
              label={adCodeStrings.filters.search}
              placeholder={adCodeStrings.filters.search}
              value={search}
              onChange={setSearch}
              onClear={() => setSearch('')}
            />
            <label className="flex items-center gap-2 text-xs font-semibold text-ink">
              <input
                type="checkbox"
                checked={excludeBlocked24h}
                onChange={(event) => setExcludeBlocked24h(event.target.checked)}
              />
              {adCodeStrings.filters.excludeBlocked24h}
            </label>
          </ManagementFilterBar>
          {mediaQuery.loading && (
            <p className="mt-2 text-xs text-muted">{commonStrings.loading}</p>
          )}
          {mediaQuery.error && (
            <div className="mt-2">
              <Alert>
                {mediaQuery.error}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="ml-2"
                  onClick={() => void mediaQuery.refetch()}
                  disabled={mediaQuery.loading}
                >
                  {commonStrings.actions.retry}
                </Button>
              </Alert>
            </div>
          )}
        </div>

        {summaryTiles.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-3 xl:grid-cols-6">
            {summaryTiles.map((tile) => (
              <div
                key={tile.label}
                className="rounded-2xl border border-border/60 bg-white px-3 py-2"
              >
                <p className="text-[11px] text-muted">{tile.label}</p>
                <p className="font-display text-lg tabular-nums text-ink">
                  {tile.value}
                </p>
                {'sub' in tile && tile.sub && (
                  <p className="text-[10px] text-amber-700">{tile.sub}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {(error || actionError) && (
          <div className="mt-4">
            <Alert>{error ?? actionError}</Alert>
          </div>
        )}

        <div className="mt-6 hidden overflow-x-auto rounded-2xl border border-border/70 bg-white lg:block table-scroll">
          <AdCodesTable
            adCodes={items}
            loading={loading}
            emptyMessage={emptyMessage}
            onOpen={handleOpen}
            onCopyUrl={handleCopyUrl}
            onEdit={handleEdit}
            onToggleArchive={handleToggleArchive}
            archivingId={archivingId}
          />
        </div>
        <div className="mt-6 lg:hidden">
          <AdCodesCardList
            adCodes={items}
            loading={loading}
            emptyMessage={emptyMessage}
            onOpen={handleOpen}
            onCopyUrl={handleCopyUrl}
            onEdit={handleEdit}
            onToggleArchive={handleToggleArchive}
            archivingId={archivingId}
            className="md:grid-cols-2"
          />
        </div>

        {!loading && hasNoAdCode && (
          <p className="mt-2 text-center text-xs text-muted">
            {adCodeStrings.empty.description}
          </p>
        )}

        {!loading && archivedHint && (
          <p className="mt-2 text-center text-xs text-muted">
            {archivedHint}{' '}
            <button
              type="button"
              className="underline transition hover:text-ink"
              onClick={() => setStatus('')}
            >
              {adCodeStrings.empty.showAll}
            </button>
          </p>
        )}

        <div className="mt-4 flex items-center justify-end gap-3">
          {data && (
            <span className="rounded-full border border-border/70 bg-white px-3 py-1 text-xs font-semibold text-muted">
              {formatResultCount(data.total, data.totalAll)}
            </span>
          )}
          <ManagementPagination
            currentPage={page}
            totalPage={totalPage}
            loading={loading}
            onPrevious={() =>
              setPagination({
                page: Math.max(1, page - 1),
                resetKey: filterResetKey,
              })
            }
            onNext={() =>
              setPagination({
                page: Math.min(totalPage, page + 1),
                resetKey: filterResetKey,
              })
            }
          />
        </div>
      </section>

      <ConfirmModal
        open={Boolean(confirmArchive)}
        textTitle={adCodeStrings.confirm.archiveTitle}
        textMessage={`${confirmArchive?.name ?? ''}\n${adCodeStrings.confirm.archiveMessage}`}
        textConfirm={adCodeStrings.confirm.archiveConfirm}
        textCancel={commonStrings.cancel}
        isLoading={archive.isPending}
        onConfirm={() => {
          if (confirmArchive) handleArchive(confirmArchive.id, true);
        }}
        onClose={() => {
          if (!archive.isPending) setConfirmArchive(null);
        }}
      />

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarMessage ?? ''}
        onClose={() => setIsSnackbarOpen(false)}
      />
    </>
  );
}
